import React from 'react';

import { Subscription as BillingSubscription } from '@futrli-sdk/billing';
import { Organisation } from '@futrli-sdk/organisations';
import { useErrorHandler } from '@futrli/errors';
import { ProductSlug } from '@futrli/shared-services';

import { fetchOrganisationById, fetchSubscription } from '../core/api';
import { PREDICT_BILLING_PRODUCT } from '../core/constants';
import { BillingProduct } from '../core/types';
import { isWorkspaceOwner as isWorkspaceOwnerFn } from './utils';

export interface UserData {
  isWorkspaceOwner: boolean;
}

export interface BillingUserData {
  subscription: BillingSubscription | null;
}

export interface ActivateProductData {
  user: UserData;
  billing: BillingUserData;
  organisation?: Organisation;
  product: BillingProduct;
}

/**
 * Hook that loads the main data needed by the activate product dialog.
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} productSlug
 * @param {boolean} isDialogOpen
 * @returns ActivateProductData.
 */
export const useLoadActivateProductData = (
  workspaceId: string,
  productSlug: ProductSlug,
  isDialogOpen: boolean,
  organisationId?: string
) => {
  const [activateProductData, setActivateProductData] = React.useState<ActivateProductData | null>(null);
  const [isActivateProductDataLoading, setIsActivateProductDataLoading] = React.useState(true);
  const onManageError = useErrorHandler();

  const resetActivateProductData = () => {
    setIsActivateProductDataLoading(true);
    setActivateProductData(null);
  };

  React.useEffect(() => {
    const loadUserData = async () => {
      try {
        setIsActivateProductDataLoading(true);
        const isWorkspaceOwner = await isWorkspaceOwnerFn(workspaceId);
        let organisation;
        if (organisationId) {
          organisation = await fetchOrganisationById(workspaceId, organisationId);
        }
        let subscription;

        // @TODO this check will be removed once we implement the activate product functionality for non workspace owners.
        if (isWorkspaceOwner) {
          subscription = await fetchSubscription(workspaceId);
        }

        setActivateProductData({
          organisation,
          user: { isWorkspaceOwner },
          billing: {
            subscription: subscription || null
          },
          product: PREDICT_BILLING_PRODUCT
        });
      } catch (e) {
        onManageError(e);
      } finally {
        setIsActivateProductDataLoading(false);
      }
    };

    if (isDialogOpen) {
      loadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen, workspaceId, organisationId, productSlug]);

  return {
    isActivateProductDataLoading,
    activateProductData,
    resetActivateProductData
  };
};
